import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

const POINTS_ERROR = "Points must be between 1 and 10,000.";
const SERIES = ["sp", "dp", "tp"];

function readError(error) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "Unable to load suggestions."
  );
}

export default function MatkaSpDpTp({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0].toLowerCase()
  );
  const [series, setSeries] = useState("");
  const [digit, setDigit] = useState("");
  const [points, setPoints] = useState("");
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [pointsError, setPointsError] = useState("");
  const [notice, setNotice] = useState("");
  const [bids, setBids] = useState([]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });
  const requestId = useRef(0);
  const timerRef = useRef(null);
  const pointsRef = useRef(null);

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );

  const cancelLookup = () => {
    requestId.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => cancelLookup(), []);

  const fetchForAnk = async (ank, id) => {
    setLoading(true);
    setFormError("");
    try {
      const data = await fetchMatkaSuggestions("sp_dp_tp", ank);
      if (id !== requestId.current) return null;
      const groups = {
        ank,
        sp: Array.isArray(data?.sp) ? data.sp.map(String) : [],
        dp: Array.isArray(data?.dp) ? data.dp.map(String) : [],
        tp: Array.isArray(data?.tp) ? data.tp.map(String) : [],
      };
      if (!groups.sp.length && !groups.dp.length && !groups.tp.length) {
        setSaved(null);
        setFormError("No combinations available for this digit");
        return null;
      }
      setSaved(groups);
      return groups;
    } catch (error) {
      if (id !== requestId.current) return null;
      setSaved(null);
      setFormError(readError(error));
      return null;
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(
        currentBids,
        nextGameType,
        (bid) => `${bid.panna}|${bid.series}`
      )
    );
  };

  const handleDigitChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 1);
    setDigit(next);
    setSaved(null);
    setFormError("");
    setLoading(false);
    cancelLookup();

    if (!next) return;

    pointsRef.current?.focus();
    const id = requestId.current + 1;
    requestId.current = id;
    timerRef.current = setTimeout(() => {
      fetchForAnk(next, id);
    }, 500);
  };

  const handlePointsChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 5);
    setPoints(next);
    if (!next) {
      setPointsError("");
      return;
    }
    const value = parseInt(next, 10);
    setPointsError(value >= 1 && value <= 10000 ? "" : POINTS_ERROR);
  };

  const handleAddBid = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (loading) return;

    if (!digit || points.trim() === "") {
      setNotice("Please fill all fields");
      return;
    }
    if (!series) {
      setNotice("Please select one option (SP, DP, or TP)");
      return;
    }

    const pointsValue = parseInt(points, 10);
    if (!Number.isInteger(pointsValue) || pointsValue < 1 || pointsValue > 10000) {
      setPointsError(POINTS_ERROR);
      return;
    }

    let groups = saved?.ank === digit ? saved : null;
    if (!groups) {
      cancelLookup();
      const id = requestId.current + 1;
      requestId.current = id;
      groups = await fetchForAnk(digit, id);
    }
    if (!groups || groups.ank !== digit) {
      setFormError(
        (current) =>
          current || "No combinations for this digit. Try another or wait for suggestions."
      );
      setNotice("No combinations for this digit. Try another or wait for suggestions.");
      return;
    }

    const panas = groups[series] || [];
    if (!panas.length) {
      setNotice(`No ${series.toUpperCase()} combinations for this digit.`);
      return;
    }

    setBids((currentBids) => {
      const nextBids = [...currentBids];
      panas.forEach((pana, index) => {
        const existingIndex = nextBids.findIndex(
          (bid) => bid.panna === pana && bid.gameType === gameType
        );
        if (existingIndex >= 0) {
          nextBids[existingIndex] = {
            ...nextBids[existingIndex],
            points: Number(nextBids[existingIndex].points) + pointsValue,
            series,
          };
          return;
        }
        nextBids.push({
          id: Date.now() + index,
          panna: pana,
          points: pointsValue,
          gameType,
          series,
        });
      });
      return nextBids;
    });
    setDigit("");
    setPoints("");
    setPointsError("");
    setFormError("");
    setSaved(null);
    setLoading(false);
    cancelLookup();
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Screen Title */}
          <h1 className="matka-panna-bulk-title mb-3">SP DP TP</h1>
          <div className="mb-4">
            <label className="matka-panna-bulk-label">GAME TYPE</label>
            <MatkaSessionSelect
              betDirection={selectedGameType?.betDirection}
              variant="bulk"
              value={gameType}
              onChange={handleGameTypeChange}
            />
          </div>

          {/* SP DP TP — one series at a time */}
          <div className="matka-checkbox-group mb-4">
            {SERIES.map((option) => (
              <label className="matka-checkbox-label" key={option}>
                <input
                  type="checkbox"
                  checked={series === option}
                  onChange={() => setSeries(option)}
                />
                <span>{option.toUpperCase()}</span>
              </label>
            ))}
          </div>

          {/* Stacked Form: DIGIT, ENTER POINTS */}
          <div className="matka-jodi-form mb-3">
            <div className="matka-jodi-input-group mb-3">
              <label className="matka-panna-bulk-label">DIGIT</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="matka-panna-bulk-input"
                placeholder="1"
                value={digit}
                onChange={handleDigitChange}
              />
              {formError && <p className="matka-sangam-error">{formError}</p>}
            </div>

            <div className="matka-jodi-input-group mb-3">
              <label className="matka-panna-bulk-label">ENTER POINTS</label>
              <input
                ref={pointsRef}
                type="text"
                inputMode="numeric"
                className="matka-panna-bulk-input"
                placeholder="Enter Points (e.g. 100)"
                value={points}
                onChange={handlePointsChange}
              />
              {pointsError && <p className="matka-sangam-error">{pointsError}</p>}
            </div>

            <div className="d-flex justify-content-end mb-4">
              <button
                type="button"
                className="matka-add-bid-btn"
                onClick={handleAddBid}
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-label="Loading"
                  ></span>
                ) : (
                  "ADD BID"
                )}
              </button>
            </div>
          </div>

          {/* Current Bids List Card */}
          <div className="matka-bids-list-card mb-4">
            <h3 className="matka-bids-list-title">Current Bids List</h3>
            <div className="table-responsive">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th className="th-panna">Panna</th>
                    <th className="th-type text-center">Game Type</th>
                    <th className="th-points text-end">Points (₹)</th>
                    <th className="th-action" aria-label="Delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id}>
                      <td className="td-panna">{bid.panna}</td>
                      <td className="td-type text-center">{bid.gameType}</td>
                      <td className="td-points text-end">{bid.points}</td>
                      <td className="td-action text-end">
                        <button
                          type="button"
                          className="matka-jodi-delete-btn"
                          onClick={() => handleDeleteBid(bid.id)}
                          aria-label={`Delete ${bid.panna}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {notice && (
        <div className="matka-sangam-popup" role="dialog" aria-modal="true">
          <div className="matka-sangam-popup-box">
            <p>{notice}</p>
            <button type="button" onClick={() => setNotice("")}>
              OK
            </button>
          </div>
        </div>
      )}

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
